import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useState } from "react";
import { dbManagerService } from "../../../api/dbManager.service.js";
import styles from "./DbManager.module.css";

const KEYSPACE_PATTERN = /^[a-z0-9-]+$/;

type CreateKeyspaceModalProps = {
  isOpen: boolean;
  family: string;
  onClose: () => void;
  onCreated: (keyspace: string) => void;
};

export function CreateKeyspaceModal({
  isOpen,
  family,
  onClose,
  onCreated,
}: CreateKeyspaceModalProps) {
  const [keyspace, setKeyspace] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setKeyspace("");
    setTouched(false);
    setSubmitting(false);
    setError(null);
  }, [isOpen]);

  const keyspaceError = useMemo(() => {
    if (!touched) return null;
    if (keyspace.length === 0) return "Slot name is required";
    if (!KEYSPACE_PATTERN.test(keyspace)) {
      return "Use only lowercase letters, digits, and dashes";
    }
    return null;
  }, [keyspace, touched]);

  const handleSubmit = async () => {
    setTouched(true);
    if (keyspaceError || keyspace.length === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await dbManagerService.createKeyspace(keyspace, family);
      onCreated(response.keyspace.keyspace);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create slot" size="small" blurBackground>
      <div className={styles.modalBody}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <p>
          Register a new empty slot in the <code>{family}</code> family before syncing into it.
        </p>

        <div className={styles.formField}>
          <label className={styles.fieldLabel} htmlFor="create-keyspace-name">
            Slot name
          </label>
          <Input
            id="create-keyspace-name"
            value={keyspace}
            onChange={event => setKeyspace(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="core-2026-04"
            error={keyspaceError ?? undefined}
            helperText={keyspaceError ? undefined : "Must match [a-z0-9-]+"}
          />
        </div>

        <div className={styles.actions}>
          <Button variant="text" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Creating..." : "Create slot"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
